import { Button, Drawer, Input, message, Popconfirm, Select, Space, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  editMemberOfBranch,
  fetchAllBranches,
  fetchBranchMembers,
} from "../../../apis/branch.api";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import { BranchMember } from "../../../interfaces/branch_member.interface";
import BranchMemberCreateForm from "./branch_member_create_form";
import BranchMemberEditForm from "./branch_member_edit_form";
import TransferClientsModal from "./transfer_clients_modal";

export interface BranchMemberProps {
  branch_id?: number;
}

const BranchMemberList = ({ branch_id }: BranchMemberProps) => {
  const { hasPermission } = usePermissions();

  const [members, setMembers] = useState<Array<any>>([]);
  const [branch_member_add_form, setBranchMemberAddForm] = useState(false);
  const [branch_member_edit_form, setBranchMemberEditForm] = useState(false);
  const [selected_member, setSelectedMember] = useState<any>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState<number | undefined>(
    undefined
  );
  const [branchOptions, setBranchOptions] = useState<
    Array<{ label: string; value: number }>
  >([]);
  const [reloadTick, setReloadTick] = useState(0);
  const [loading, setLoading] = useState(false);
  const [transfer_source, setTransferSource] = useState<any>(null);
  const isAdmin = hasPermission("admin");

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    fetchAllBranches().then((res: any) => {
      if (cancelled) return;
      const opts = (res ?? []).map((b: any) => ({
        label: b?.branch_name,
        value: b?.id,
      }));
      setBranchOptions(opts);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const showBranchMemberAddDrawer = useCallback(() => {
    setBranchMemberAddForm(true);
  }, []);
  const showBranchMemberEditDrawer = useCallback(() => {
    setBranchMemberEditForm(true);
  }, []);

  const onMemberAddDrawerClose = useCallback(() => {
    setBranchMemberAddForm(false);
  }, []);
  const onMemberEditDrawerClose = useCallback(() => {
    setBranchMemberEditForm(false);
  }, []);
  const onMemberAddSaved = useCallback(() => {
    setBranchMemberAddForm(false);
    setReloadTick((t) => t + 1);
  }, []);
  const onMemberEditSaved = useCallback(() => {
    setBranchMemberEditForm(false);
    setReloadTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params: any = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (branchFilter !== undefined) params.branch = branchFilter;
    fetchBranchMembers(params)
      .then((res) => {
        if (cancelled) return;
        const list: Array<any> = (res ?? []).map((member: any) => ({
          ...member?.user,
          user_permission: member?.user_permission,
          user_permission_detail: member?.user_permission_detail,
          id: member.id,
          category_name: member?.category_name,
          branch: member?.branch,
        }));
        setMembers(list);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick, branch_id, debouncedSearch, branchFilter]);

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const permA = a?.user_permission_detail?.permission_name ?? "";
      const permB = b?.user_permission_detail?.permission_name ?? "";
      const permCmp = permA.localeCompare(permB);
      if (permCmp !== 0) return permCmp;
      const branchA = a?.branch?.[0]?.branch_name ?? "";
      const branchB = b?.branch?.[0]?.branch_name ?? "";
      return branchA.localeCompare(branchB);
    });
  }, [members]);

  const columns: ColumnsType<BranchMember> = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "f_name",
        key: "f_name",
        width: 180,
        ellipsis: true,
      },
      {
        title: "Branch Name",
        key: "branch_name",
        width: 160,
        ellipsis: true,
        render: (record: any) => {
          return record?.branch?.[0]?.branch_name ?? "N/A";
        },
      },
      {
        title: "Permission",
        key: "permission",
        width: 160,
        ellipsis: true,
        render: (record: any) =>
          record?.user_permission_detail?.permission_name ?? "N/A",
      },
      {
        title: "Phone Number",
        dataIndex: "contact",
        key: "contact",
        width: 140,
      },
      {
        title: "Email",
        key: "email",
        dataIndex: "email",
        width: 240,
        ellipsis: true,
      },
      {
        title: "Action",
        key: "action",
        width: 200,
        fixed: "right" as const,
        render: (_, record) => (
          <Space size="middle">
            <Button
              size="small"
              className="fw-bold"
              type="primary"
              disabled={!hasPermission("branch_details.update")}
              onClick={() => {
                showBranchMemberEditDrawer();
                setSelectedMember(record);
              }}
            >
              Edit Details
            </Button>

            {isAdmin && (
              <Button
                size="small"
                className="fw-bold"
                onClick={() => setTransferSource(record)}
              >
                Transfer Clients
              </Button>
            )}

            <Popconfirm
              disabled={!hasPermission("branch_details.update")}
              onConfirm={async () => {
                if (!hasPermission("branch_details.update")) {
                  message.error("You don't have permission to delete this member");
                  return;
                }
                const ok = await editMemberOfBranch(
                  { status: false },
                  record.id
                );
                if (ok) {
                  message.success("Deleted successfully");
                  setReloadTick((t) => t + 1);
                }
              }}
              title="Are you sure you want to delete this user?"
            >
              <Button
                disabled={!hasPermission("branch_details.update")}
                size="small"
                type="primary"
                danger
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [hasPermission, showBranchMemberEditDrawer, isAdmin]
  );

  return (
    <div className=" p-3 py-2">
      <div className="d-flex justify-content-between align-items-center pb-3">
        <div className="th-font-size-18 th-font-weight-600 primary-color-text align-middle">
          <div>Members</div>
        </div>
        <div className="d-flex gap-2">
          <Select
            style={{ minWidth: 180 }}
            placeholder="Filter by branch"
            value={branchFilter}
            onChange={(v) => setBranchFilter(v)}
            options={branchOptions}
            allowClear
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
          <Input.Search
            type="text"
            style={{ maxWidth: 220, minWidth: 180 }}
            placeholder="Search name, email, phone"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            allowClear
          />
          <Button
            size="middle"
            className="fw-bold"
            type="primary"
            disabled={!hasPermission("branch_details.create")}
            onClick={showBranchMemberAddDrawer}
          >
            Add Member
          </Button>
        </div>
      </div>
      <div className="row gap-3">
        <Table
          columns={columns}
          dataSource={sortedMembers}
          pagination={false}
          loading={loading}
          rowKey={"id"}
          scroll={{
            x: "100%",
          }}
        />
      </div>
      <Drawer
        title="Add Member"
        placement="right"
        closable={false}
        onClose={onMemberAddDrawerClose}
        open={branch_member_add_form}
        destroyOnClose
      >
        <BranchMemberCreateForm
          onMemberAddDrawerClose={onMemberAddSaved}
          branch_id={branch_id}
        />
      </Drawer>
      <Drawer
        title="Edit Member"
        placement="right"
        closable={false}
        onClose={onMemberEditDrawerClose}
        open={branch_member_edit_form}
        destroyOnClose
      >
        <BranchMemberEditForm
          onMemberEditDrawerClose={onMemberEditSaved}
          selected_member={selected_member}
        />
      </Drawer>
      <TransferClientsModal
        open={!!transfer_source}
        source={transfer_source}
        onClose={() => setTransferSource(null)}
      />
    </div>
  );
};

export default BranchMemberList;
